import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatMXN } from "../lib/format";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login?redirect=/checkout");
  }, [user, loading]);

  useEffect(() => {
    api.get("/paypal/config").then((res) => setConfig(res.data));
  }, []);

  useEffect(() => {
    if (items.length === 0 && !done) navigate("/");
  }, [items, done]);

  const createOrderOnServer = async () => {
    const res = await api.post("/orders", {
      items: items.map((i) => ({
        product_id: i.product_id,
        title: i.title,
        price: i.price,
        quantity: i.quantity,
        image: i.image,
      })),
    });
    return res.data;
  };

  const finishSuccess = () => {
    setDone(true);
    clear();
    toast.success("¡Pago completado! Bienvenido a VALORA.");
  };

  const handleMockPay = async () => {
    try {
      setProcessing(true);
      const order = await createOrderOnServer();
      await api.post(`/orders/${order.id}/capture`);
      finishSuccess();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al procesar el pago");
    } finally {
      setProcessing(false);
    }
  };

  if (done)
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <CheckCircle2 className="mx-auto mb-5 h-20 w-20 text-brand-forest" />
        <h1 className="font-display text-3xl font-extrabold text-brand-navy">¡Inscripción confirmada!</h1>
        <p className="mt-3 text-slate-600">
          Tu pago se procesó correctamente. Recibirás los accesos y detalles de tu certificación por correo.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <button onClick={() => navigate("/mis-cursos")} className="rounded-full brand-navy px-6 py-3 text-sm font-bold text-white">
            Ver mis pedidos
          </button>
          <button onClick={() => navigate("/")} className="rounded-full border border-slate-300 px-6 py-3 text-sm font-bold text-brand-navy">
            Seguir explorando
          </button>
        </div>
      </div>
    );

  return (
    <div className="mx-auto max-w-5xl px-6 py-12">
      <h1 className="font-display text-3xl font-extrabold text-brand-navy">Finalizar compra</h1>
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_1fr]">
        {/* Summary */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display text-lg font-bold text-brand-navy">Resumen del pedido</h2>
          <div className="mt-4 space-y-4">
            {items.map((i) => (
              <div key={i.product_id} className="flex items-center gap-3">
                <img src={i.image} alt={i.title} className="h-14 w-14 flex-none rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-1 text-sm font-semibold text-brand-navy">{i.title}</p>
                  <p className="text-xs text-slate-500">Cantidad: {i.quantity}</p>
                </div>
                <span className="text-sm font-bold text-brand-navy">{formatMXN(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 flex justify-between border-t border-slate-100 pt-4">
            <span className="font-semibold text-brand-navy">Total a pagar</span>
            <span className="font-display text-2xl font-extrabold text-brand-navy">{formatMXN(subtotal)}</span>
          </div>
        </div>

        {/* Payment */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display text-lg font-bold text-brand-navy">Método de pago</h2>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
            <Lock className="h-3.5 w-3.5" /> Transacción segura y cifrada
          </p>

          <div className="mt-6" data-testid="paypal-button-container">
            {!config ? (
              <div className="text-sm text-slate-400">Cargando opciones de pago…</div>
            ) : config.configured ? (
              <PayPalScriptProvider
                options={{ clientId: config.clientId, currency: "MXN", intent: "capture" }}
              >
                <PayPalButtons
                  style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal" }}
                  createOrder={async () => {
                    const order = await createOrderOnServer();
                    return order.paypal_order_id;
                  }}
                  onApprove={async (data) => {
                    try {
                      setProcessing(true);
                      const orders = await api.get("/orders");
                      const match = orders.data.find((o) => o.paypal_order_id === data.orderID);
                      if (match) await api.post(`/orders/${match.id}/capture`);
                      finishSuccess();
                    } catch (e) {
                      toast.error("No se pudo confirmar el pago");
                    } finally {
                      setProcessing(false);
                    }
                  }}
                  onError={() => toast.error("Error con PayPal")}
                />
              </PayPalScriptProvider>
            ) : (
              <div>
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  Modo demostración: PayPal aún no está configurado con llaves de producción.
                  Puedes simular el pago para probar el flujo completo.
                </div>
                <button
                  data-testid="paypal-mock-pay-button"
                  disabled={processing}
                  onClick={handleMockPay}
                  className="w-full rounded-full py-3.5 text-sm font-bold text-slate-900 transition-transform hover:scale-[1.02] disabled:opacity-60"
                  style={{ backgroundColor: "#ffc439" }}
                >
                  {processing ? "Procesando…" : `Pagar ${formatMXN(subtotal)} (Demo PayPal)`}
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 space-y-2 border-t border-slate-100 pt-5 text-xs text-slate-500">
            <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-forest" /> Tus datos están protegidos</p>
            <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-forest" /> Certificación con validez oficial</p>
          </div>
        </div>
      </div>
    </div>
  );
}
